import {
  MAX_POSTER_BYTES,
  MAX_SOURCE_BYTES,
  POSTER_CONTENT_TYPES,
  PosterContentType,
  SOURCE_CONTENT_TYPES,
  SourceContentType,
} from '../model/dto/video/video-constants';
import { CreateVideoRequest } from '../model/dto/video/video-requests';
import {
  PosterFileRejection,
  StagedVideo,
  VideoDimensions,
  VideoFileRejection,
  VideoOrientation,
} from '../model/type/video-file.types';
import {
  isPlacementPositionValid,
  toCreatePlacementFields,
} from './video-placement-utils';

const DIMENSIONS_TIMEOUT_MS = 10000;

const EXTENSION_CONTENT_TYPES: Record<string, SourceContentType> = {
  mp4: 'video/mp4',
  mov: 'video/quicktime',
};

export const ORIENTATION_DIMENSIONS: Record<VideoOrientation, VideoDimensions> =
  {
    portrait: { width: 1080, height: 1920 },
    landscape: { width: 1920, height: 1080 },
  };

const isSourceContentType = (type: string): type is SourceContentType =>
  (SOURCE_CONTENT_TYPES as readonly string[]).includes(type);

const isPosterContentType = (type: string): type is PosterContentType =>
  (POSTER_CONTENT_TYPES as readonly string[]).includes(type);

const fileExtension = (name: string): string =>
  name.includes('.') ? name.split('.').pop()?.toLowerCase() ?? '' : '';

export const contentTypeForFile = (file: File): SourceContentType | null => {
  if (file.type) {
    return isSourceContentType(file.type) ? file.type : null;
  }

  return EXTENSION_CONTENT_TYPES[fileExtension(file.name)] ?? null;
};

export const titleFromFileName = (name: string): string =>
  name.replace(/\.[^.]*$/, '');

export const validateVideoFile = (file: File): VideoFileRejection | null => {
  if (!contentTypeForFile(file)) {
    return 'unsupportedType';
  }

  if (file.size === 0) {
    return 'empty';
  }

  return file.size > MAX_SOURCE_BYTES ? 'tooLarge' : null;
};

export const validatePosterFile = (file: File): PosterFileRejection | null => {
  if (!isPosterContentType(file.type)) {
    return 'unsupportedType';
  }

  if (file.size === 0) {
    return 'empty';
  }

  return file.size > MAX_POSTER_BYTES ? 'tooLarge' : null;
};

export const posterContentType = (file: File): PosterContentType | null =>
  isPosterContentType(file.type) ? file.type : null;

export const readVideoDimensions = (
  file: File
): Promise<VideoDimensions | null> =>
  new Promise((resolve) => {
    const url = URL.createObjectURL(file);
    const video = document.createElement('video');

    const finish = (dimensions: VideoDimensions | null) => {
      clearTimeout(timer);
      video.onloadedmetadata = null;
      video.onerror = null;
      video.removeAttribute('src');
      URL.revokeObjectURL(url);
      resolve(dimensions);
    };

    const timer = setTimeout(() => finish(null), DIMENSIONS_TIMEOUT_MS);

    video.preload = 'metadata';
    video.muted = true;
    video.onloadedmetadata = () =>
      finish(
        video.videoWidth && video.videoHeight
          ? { width: video.videoWidth, height: video.videoHeight }
          : null
      );
    video.onerror = () => finish(null);
    video.src = url;
  });

const THUMBNAIL_WIDTH = 160;
const THUMBNAIL_SEEK_SECONDS = 1;

const thumbnailSeekTime = (duration: number): number =>
  Number.isFinite(duration) && duration > 0
    ? Math.min(THUMBNAIL_SEEK_SECONDS, duration / 2)
    : 0;

export const readVideoThumbnail = (file: File): Promise<string | null> =>
  new Promise((resolve) => {
    const url = URL.createObjectURL(file);
    const video = document.createElement('video');

    const finish = (thumbnail: string | null) => {
      clearTimeout(timer);
      video.onloadedmetadata = null;
      video.onseeked = null;
      video.onerror = null;
      video.removeAttribute('src');
      URL.revokeObjectURL(url);
      resolve(thumbnail);
    };

    const capture = () => {
      const canvas = document.createElement('canvas');
      const context = canvas.getContext('2d');

      if (!context || !video.videoWidth || !video.videoHeight) {
        finish(null);
        return;
      }

      canvas.width = THUMBNAIL_WIDTH;
      canvas.height = Math.round(
        (THUMBNAIL_WIDTH * video.videoHeight) / video.videoWidth
      );

      try {
        context.drawImage(video, 0, 0, canvas.width, canvas.height);
        finish(canvas.toDataURL('image/jpeg'));
      } catch {
        finish(null);
      }
    };

    const timer = setTimeout(() => finish(null), DIMENSIONS_TIMEOUT_MS);

    video.preload = 'auto';
    video.muted = true;
    video.onloadedmetadata = () => {
      video.currentTime = thumbnailSeekTime(video.duration);
    };
    video.onseeked = capture;
    video.onerror = () => finish(null);
    video.src = url;
  });

export const isSameFile = (first: File, second: File): boolean =>
  first.name === second.name &&
  first.size === second.size &&
  first.lastModified === second.lastModified;

export const stagedVideoDimensions = ({
  dimensions,
  orientation,
}: StagedVideo): VideoDimensions | null => {
  if (dimensions && dimensions !== 'reading') {
    return dimensions;
  }

  return orientation ? ORIENTATION_DIMENSIONS[orientation] : null;
};

export const toCreateVideoRequest = (
  staged: StagedVideo
): CreateVideoRequest | null => {
  const dimensions = stagedVideoDimensions(staged);
  const title = staged.title.trim();

  if (!dimensions || !title || !isPlacementPositionValid(staged.placement)) {
    return null;
  }

  const description = staged.description.trim();

  return {
    title,
    ...(description ? { description } : {}),
    content_type: staged.contentType,
    size_bytes: staged.file.size,
    source_width: dimensions.width,
    source_height: dimensions.height,
    ...toCreatePlacementFields(staged.placement),
  };
};

export const formatVideoDuration = (seconds: number | null): string => {
  if (seconds === null) {
    return '–';
  }

  const rounded = Math.round(seconds);
  const minutes = Math.floor(rounded / 60);

  return `${minutes}:${String(rounded % 60).padStart(2, '0')}`;
};

const BYTES_PER_MB = 1024 ** 2;

export const formatMegabytes = (bytes: number): string =>
  (bytes / BYTES_PER_MB).toFixed(1);
